/**
 * Secure markdown parsing utility that returns React elements.
 *
 * This utility replaces unsafe HTML string injection patterns with
 * XSS-safe React elements. React automatically escapes text content,
 * making this approach inherently secure.
 *
 * Currently supports:
 * - **bold** syntax → <strong> elements
 *
 * @example
 * ```tsx
 * // Basic usage
 * <p>{parseMarkdown("Hello **world**!")}</p>
 * // Renders: <p>Hello <strong>world</strong>!</p>
 *
 * // With custom className for bold text
 * <p>{parseMarkdown("**Important** notice", "font-bold text-red-500")}</p>
 * ```
 */
import { ReactNode } from 'react'

/**
 * Parse markdown-like text and return React elements.
 *
 * Currently supports:
 * - **bold** → <strong className={boldClassName}>bold</strong>
 *
 * @param text - The text containing markdown syntax
 * @param boldClassName - Optional CSS class for bold elements (default: 'font-bold')
 * @returns Array of React nodes (strings and elements)
 */
export function parseMarkdown(text: string, boldClassName = 'font-bold'): ReactNode[] {
  if (!text) {
    return []
  }

  // Split text by **bold** patterns while preserving the pattern groups
  // Regex: match **...** and capture the content inside
  const parts = text.split(/(\*\*.*?\*\*)/g)

  return parts.map((part, index) => {
    // Check if this part is a bold pattern
    const boldMatch = part.match(/^\*\*(.*?)\*\*$/)

    if (boldMatch) {
      // Return a <strong> element with the captured text
      // React automatically escapes the content, preventing XSS
      return (
        <strong key={index} className={boldClassName}>
          {boldMatch[1]}
        </strong>
      )
    }

    // Return plain text (React automatically escapes it)
    return part
  }).filter(part => part !== '') // Remove empty strings from split
}

/**
 * Parse a paragraph containing markdown and return as React nodes.
 * This is a convenience wrapper around parseMarkdown for single paragraphs.
 *
 * @param text - The paragraph text
 * @param boldClassName - Optional CSS class for bold elements
 * @returns React nodes suitable for rendering inside a <p> element
 */
export function parseMarkdownParagraph(
  text: string,
  boldClassName = 'font-bold'
): ReactNode {
  const nodes = parseMarkdown(text, boldClassName)
  return <>{nodes}</>
}

/**
 * Parse multiple paragraphs (split by double newlines) and return as React elements.
 * Each paragraph is wrapped in a <p> element.
 *
 * @param text - The full text with paragraphs separated by double newlines
 * @param paragraphClassName - CSS class for <p> elements
 * @param boldClassName - CSS class for bold elements
 * @returns Array of <p> elements
 */
export function parseMarkdownParagraphs(
  text: string,
  paragraphClassName = '',
  boldClassName = 'font-bold'
): ReactNode[] {
  if (!text) {
    return []
  }

  const paragraphs = text.split('\n\n')

  return paragraphs.map((paragraph, index) => (
    <p key={index} className={paragraphClassName}>
      {parseMarkdown(paragraph.trim(), boldClassName)}
    </p>
  ))
}
